<template>
  <div class="service-wrap pt-20 pb-20">
    <div class="container">

      <div class="row">
        <div class="col-xl-3 col-lg-4 col-md-6" v-for="(formateur, index) in formateurs" :key="index">
          <div class="team-card style1">
            <div 
              class="team-img"
              @click="openModal(formateur)"
            >
              <!-- Photo ou initiales -->
              <div 
                v-if="formateur.user?.urlImage"
                :style="{
                  height: '300px',
                  backgroundImage: `url(${getImageUrl(formateur.user.urlImage)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }"
              ></div>
              <div 
                v-else
                class="no-photo-placeholder"
              >
                <span class="card-initials">
                  {{ getInitials(formateur.nom, formateur.prenom) }}
                </span>
              </div>

              <div class="team-overlay">
                <i class="flaticon-eye"></i>
              </div>
            </div>
            <div class="team-info text-center mt-3">
              <h4>{{ formateur.nom }} {{ formateur.prenom }}</h4>
              <span class="team-designation">{{ formateur.specialite || 'Formateur' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>

    <!-- Modal identique à celui des Disciples -->
    <div v-if="showModal && selectedFormateur" class="photo-modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <span class="close-btn" @click="closeModal">&times;</span>
        
        <!-- Image ou initiales -->
        <div v-if="selectedFormateur.user?.urlImage" class="modal-image-container">
          <img :src="getImageUrl(selectedFormateur.user.urlImage)" :alt="selectedFormateur.nom" />
        </div>
        <div v-else class="modal-initials-container">
          <div class="initials-circle">
            {{ getInitials(selectedFormateur.nom, selectedFormateur.prenom) }}
          </div>
        </div>
        
        <div class="modal-info">
          <h3>{{ selectedFormateur.nom }} {{ selectedFormateur.prenom }}</h3>
          <p class="specialite">{{ selectedFormateur.specialite || 'Formateur' }}</p>
          
          <!-- Informations supplémentaires -->
          <div class="details" v-if="selectedFormateur">
            <p v-if="selectedFormateur.email"><strong>Email:</strong> {{ selectedFormateur.email }}</p>
            <p v-if="selectedFormateur.telephone"><strong>Téléphone:</strong> {{ selectedFormateur.telephone }}</p>
            <p v-if="selectedFormateur.dateNaissance"><strong>Date de naissance:</strong> {{ selectedFormateur.dateNaissance }}</p>
            <p v-if="selectedFormateur.lieuNaissance"><strong>Lieu de naissance:</strong> {{ selectedFormateur.lieuNaissance }}</p>
            <p v-if="selectedFormateur.adresse"><strong>Adresse:</strong> {{ selectedFormateur.adresse }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ApiService from "@/services/ApiService";
import { getUrlApiForFiles, error } from "@/utils/utils";
import { defineComponent, ref, onMounted } from "vue";
import PaginationComponent from '@/components/Utilities/Pagination.vue';

interface Formateur {
  id: number;
  nom: string;
  prenom: string;
  photo?: string;
  specialite?: string;
  email?: string;
  telephone?: string;
  dateNaissance?: string;
  lieuNaissance?: string;
  adresse?: string;
  user ? : User
}
interface User{
  urlImage?:string
}
export default defineComponent({
  name: 'FormaTeurs',
  components: { PaginationComponent },
  setup() {
    const formateurs = ref<Array<Formateur>>([]);
    const isLoading = ref(false);
    const showModal = ref(false);
    const selectedFormateur = ref<Formateur | null>(null);

    // PAGINATION
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(12);
    const totalElements = ref(0);

    const handlePaginate = ({ page_, limit_ }) => {
      page.value = page_;
      getFormateurs(page_, limit_);
    };

    // Récupérer la liste des formateurs
    function getFormateurs(pageNum = 1, limi = 12) {
      isLoading.value = true;
      return ApiService.get(`/all/professeurs?page=${pageNum}&limit=${limi}`)
        .then(({ data }) => {
          formateurs.value = data.data.data;
          totalPages.value = data.data.totalPages;
          limit.value = data.data.limit;
          totalElements.value = data.data.totalElements;
        })
        .catch(({ response }) => {
          error(response?.data?.message || "Erreur de chargement");
          formateurs.value = [];
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    function getImageUrl(filename?: string) : string {
  if (!filename) return ''
  return `${ApiService.vueInstance.axios.defaults.baseURL?.split("api")[0]}uploads/Users/${filename}`; }

    function openModal(formateur: Formateur) {
      selectedFormateur.value = formateur;
      showModal.value = true;
    }

    function closeModal() {
      showModal.value = false;
      selectedFormateur.value = null;
    }

    // Fonction pour obtenir les initiales
    function getInitials(nom: string, prenom: string): string {
      const firstLetter = prenom?.charAt(0)?.toUpperCase() || '';
      const lastLetter = nom?.charAt(0)?.toUpperCase() || '';
      return `${firstLetter}${lastLetter}`;
    }

    onMounted(() => {
      getFormateurs();
    });

    return {
      formateurs,
      isLoading,
      showModal,
      selectedFormateur,
      getUrlApiForFiles,
      page,
      totalPages,
      limit,
      totalElements,
      handlePaginate,
      openModal,
      closeModal,
      getInitials,
      getImageUrl
    };
  },
});
</script>

<style scoped>
.team-card {
  transition: transform 0.3s ease;
  margin-bottom: 20px;
}

.team-card:hover {
  transform: translateY(-5px);
}

.team-img {
  position: relative;
  overflow: hidden;
  height: 300px;
  border-radius: 10px;
  cursor: pointer;
}

.team-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 10px;
}

.team-img:hover .team-overlay {
  opacity: 1;
}

.team-overlay i {
  font-size: 32px;
  color: white;
}

.team-info h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #1a1a1a;
}

.team-designation {
  font-size: 14px;
  color: #666;
}

.no-photo-placeholder {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border-radius: 10px;
  border: 2px dashed #d1d5db;
}

.card-initials {
  font-size: 60px;
  font-weight: bold;
  color: #6b7280;
}

.photo-modal {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  position: relative;
  max-width: 500px; 
  width: 90%;
  max-height: 90vh;
  background: white;
  border-radius: 10px;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    transform: translateY(-50px);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-image-container img {
  width: 100%;
  height: auto;
  max-height: 350px; 
  object-fit: cover;
  display: block;
  border-radius: 10px 10px 0 0;
}

.modal-initials-container {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f9fafb;
}

.initials-circle {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
  font-weight: bold;
  color: #4b5563;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 25px;
  color: white;
  font-size: 40px;
  font-weight: bold;
  cursor: pointer;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.5);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.3s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.modal-info {
  padding: 25px; 
  text-align: center;
}

.modal-info h3 {
  font-size: 24px; 
  font-weight: 600;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.modal-info .specialite {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  font-style: italic;
}

.modal-info .details {
  text-align: left;
  max-width: 400px; 
  margin: 0 auto;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.modal-info .details p {
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.modal-info .details strong {
  color: #000;
  margin-right: 5px;
}
</style>